// =============================================================================
// santinho.ts — Santinho eleitoral compartilhável (issue #96): a partida vira
// um card pixel art estilo panfleto de campanha, gerado num canvas offscreen
// (PNG puro, sem backend). Moldura "PEÇA E DIVULGUE", sprite do candidato,
// número de urna, stats da run e o título honorífico da comparação intra-chapa.
// Botões: COPIAR SANTINHO (clipboard de imagem) e DIVULGAR (Web Share, mobile).
// =============================================================================
import { CHARACTER_TABLE } from '../../../shared/characters';
import { SHEET_CHAR_IDS } from '../charSheets';
import { spriteFactory } from '../sprites';

// O que cada membro da chapa precisa expor pra disputa dos títulos honoríficos
export interface ChapaMembro {
  id: string;
  kills: number;
  revives: number;
  zombified: boolean;
}

// Dados de UMA run de UM jogador — o santinho é sempre o do jogador local
export interface SantinhoRun {
  name: string;
  charId: string;
  kills: number;
  level: number;
  timeSurvived: number;   // segundos
  titulo: string;         // título honorífico (ver honorifico())
  resultado: string;      // linha da apuração, ex.: "87% CONCLUÍDA"
  resultadoBom: boolean;  // pinta o resultado de verde (vitória) ou vermelho
}

// -----------------------------------------------------------------------------
// Títulos honoríficos (comparação social DENTRO da chapa — issue #96)
// -----------------------------------------------------------------------------
export function honorifico(you: ChapaMembro, chapa: ChapaMembro[]): string {
  if (you.zombified) return 'O Vira-Casaca';                    // virou zumbi político
  const maxRevives = Math.max(...chapa.map((p) => p.revives));
  if (maxRevives > 0 && you.revives === maxRevives) return 'O Cristo da Coligação';
  const maxKills = Math.max(...chapa.map((p) => p.kills));
  if (you.kills === maxKills) return 'O Mais Votado da Chapa';
  const minKills = Math.min(...chapa.map((p) => p.kills));
  if (chapa.length > 1 && you.kills === minKills) return 'O Laranja';
  return 'O Cabo Eleitoral';                                    // meio da tabela: militância raiz
}

// -----------------------------------------------------------------------------
// Desenho do santinho (canvas offscreen 480x640, tudo pixelado)
// -----------------------------------------------------------------------------
const W = 480;
const H = 640;
const CELL = 64;              // célula dos atlas de personagem (charSheets.ts)

// Paleta de gráfica de esquina: papel barato + verde-amarelo institucional
const PAPEL = '#f4f1ea';
const TINTA = '#16324a';
const VERDE = '#1e9e4e';
const AMARELO = '#f7d117';
const VERMELHO = '#d42a2a';
const PRETO = '#111111';
const TINTA_FRACA = '#5a6b80';

const fmtTempo = (secs: number): string =>
  `${Math.floor(secs / 60)}m${String(Math.floor(secs) % 60).padStart(2, '0')}s`;

// Moldura vazada (quatro tiras) — o "filete" da gráfica
function filete(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, t: number, cor: string): void {
  g.fillStyle = cor;
  g.fillRect(x, y, w, t);
  g.fillRect(x, y + h - t, w, t);
  g.fillRect(x, y, t, h);
  g.fillRect(x + w - t, y, t, h);
}

function texto(g: CanvasRenderingContext2D, t: string, x: number, y: number,
  px: number, cor: string, align: CanvasTextAlign = 'center'): void {
  g.font = `${px}px 'VT323', monospace`;
  g.fillStyle = cor;
  g.textAlign = align;
  g.textBaseline = 'alphabetic';
  g.fillText(t, x, y);
}

// Encolhe a fonte até o texto caber na largura (nome de urna comprido existe)
function textoAjustado(g: CanvasRenderingContext2D, t: string, x: number, y: number,
  pxMax: number, maxW: number, cor: string): void {
  let px = pxMax;
  g.font = `${px}px 'VT323', monospace`;
  while (px > 18 && g.measureText(t).width > maxW) {
    px -= 2;
    g.font = `${px}px 'VT323', monospace`;
  }
  texto(g, t, x, y, px, cor);
}

// A fonte pixel precisa estar carregada ANTES do fillText (senão sai Courier)
async function garanteFonte(): Promise<void> {
  try {
    await Promise.all([
      document.fonts.load("40px 'VT323'"),
      document.fonts.load("20px 'VT323'"),
    ]);
  } catch { /* offline/fonte bloqueada: o monospace do sistema segura o santinho */ }
}

// Retrato: atlas do personagem (frame parado de frente); sem atlas, cai no
// sprite procedural que o cliente já gera
function carregaSheet(charId: string): Promise<HTMLImageElement | null> {
  if (!SHEET_CHAR_IDS.includes(charId)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `/assets/${charId}_sheet.png`;
  });
}

export async function desenhaSantinho(run: SantinhoRun): Promise<HTMLCanvasElement> {
  await garanteFonte();
  const sheet = await carregaSheet(run.charId);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('canvas 2d indisponível');
  g.imageSmoothingEnabled = false;

  // Papel + moldura verde-amarela de santinho raiz
  g.fillStyle = VERDE;
  g.fillRect(0, 0, W, H);
  g.fillStyle = PAPEL;
  g.fillRect(12, 12, W - 24, H - 24);
  filete(g, 18, 18, W - 36, H - 36, 3, AMARELO);

  // Faixa "PEÇA E DIVULGUE" — o clássico do papel jogado no chão da seção
  g.fillStyle = TINTA;
  g.fillRect(26, 28, W - 52, 44);
  texto(g, 'PEÇA E DIVULGUE', W / 2, 60, 30, AMARELO);

  // Retrato oficial de campanha (moldura branca com o sprite pixelado 3x)
  const boxSize = 212;
  const boxX = (W - boxSize) / 2;
  const boxY = 84;
  g.fillStyle = '#ffffff';
  g.fillRect(boxX, boxY, boxSize, boxSize);
  filete(g, boxX, boxY, boxSize, boxSize, 4, TINTA);
  const spr = 192;
  const sprX = boxX + (boxSize - spr) / 2;
  const sprY = boxY + (boxSize - spr) / 2;
  if (sheet) {
    // col 1 da linha 0 = parado de frente (layout padrão dos atlas)
    g.drawImage(sheet, CELL, 0, CELL, CELL, sprX, sprY, spr, spr);
  } else {
    try {
      const c = spriteFactory.canvas(run.charId);
      const s = Math.max(1, Math.floor(spr / Math.max(c.width, c.height)));
      const w = c.width * s;
      const h = c.height * s;
      g.drawImage(c, sprX + (spr - w) / 2, sprY + (spr - h) / 2, w, h);
    } catch { /* sem sprite: a moldura vazia também é muito Brasil */ }
  }

  // Nome de urna + título honorífico da chapa
  textoAjustado(g, run.name.toUpperCase(), W / 2, 336, 40, W - 96, TINTA);
  textoAjustado(g, `“${run.titulo.toUpperCase()}”`, W / 2, 368, 26, W - 96, VERMELHO);

  // Número de urna gigante, em teclas pretas de urna eletrônica
  const numero = String(CHARACTER_TABLE[run.charId]?.numero ?? 0);
  texto(g, 'NÚMERO DE URNA', W / 2, 394, 16, TINTA_FRACA);
  const dW = 50;
  const dH = 62;
  const gap = 8;
  const total = numero.length * dW + (numero.length - 1) * gap;
  let dx = (W - total) / 2;
  for (const dig of numero) {
    g.fillStyle = PRETO;
    g.fillRect(dx, 402, dW, dH);
    texto(g, dig, dx + dW / 2, 402 + 48, 52, AMARELO);
    dx += dW + gap;
  }

  // Boletim da run: os números que valem mais que qualquer pesquisa de boca de urna
  g.fillStyle = TINTA_FRACA;
  g.fillRect(48, 480, W - 96, 2);
  const linhas: [string, string, string][] = [
    ['ADVERSÁRIOS NEUTRALIZADOS', run.kills.toLocaleString('pt-BR'), TINTA],
    ['NÍVEL DA CHAPA', String(run.level), TINTA],
    ['TEMPO DE MANDATO', fmtTempo(run.timeSurvived), TINTA],
    ['APURAÇÃO', run.resultado, run.resultadoBom ? VERDE : VERMELHO],
  ];
  let ly = 504;
  for (const [rotulo, valor, cor] of linhas) {
    texto(g, rotulo, 48, ly, 19, TINTA, 'left');
    texto(g, valor, W - 48, ly, 21, cor, 'right');
    ly += 22;
  }

  // Rodapé institucional da gráfica
  g.fillStyle = VERDE;
  g.fillRect(26, 574, W - 52, 44);
  texto(g, 'BRASIL SOBREVIVENTE — TERCEIRO TURNO', W / 2, 593, 20, PAPEL);
  texto(g, 'PROPAGANDA ELEITORAL GRATUITA (DE VERDADE)', W / 2, 611, 15, '#c9e8d2');

  return canvas;
}

// -----------------------------------------------------------------------------
// Painel do santinho nas telas de fim de jogo: preview + COPIAR + DIVULGAR.
// Sem ids fixos — os elementos são resolvidos DENTRO do container, então o
// mesmo painel serve pro game over e pro match cleared.
// -----------------------------------------------------------------------------
export class SantinhoPanel {
  private blob: Blob | null = null;

  render(container: HTMLElement, run: SantinhoRun): void {
    this.blob = null;
    container.innerHTML = `
      <div class="santinho-panel">
        <img class="santinho-preview" alt="Santinho eleitoral da sua campanha" />
        <div class="santinho-acoes">
          <button type="button" class="btn btn-yellow btn-mini santinho-copy" disabled>COPIAR SANTINHO</button>
          <button type="button" class="btn btn-green btn-mini santinho-share" style="display:none">DIVULGAR NO ZAP</button>
        </div>
        <p class="santinho-msg">Rodando a gráfica da campanha...</p>
      </div>`;
    const img = container.querySelector<HTMLImageElement>('.santinho-preview');
    const btnCopy = container.querySelector<HTMLButtonElement>('.santinho-copy');
    const btnShare = container.querySelector<HTMLButtonElement>('.santinho-share');
    const msg = container.querySelector<HTMLElement>('.santinho-msg');
    if (!img || !btnCopy || !btnShare || !msg) return;
    void this.monta(run, img, btnCopy, btnShare, msg);
  }

  private async monta(run: SantinhoRun, img: HTMLImageElement,
    btnCopy: HTMLButtonElement, btnShare: HTMLButtonElement, msg: HTMLElement): Promise<void> {
    try {
      const canvas = await desenhaSantinho(run);
      img.src = canvas.toDataURL('image/png');
      this.blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
    } catch {
      msg.textContent = 'A gráfica da campanha falhou. O santinho fica pra próxima eleição.';
      return;
    }
    if (!this.blob) return;
    msg.textContent = '';
    btnCopy.disabled = false;
    btnCopy.onclick = () => void this.copiar(msg);

    // Web Share API (mobile): manda o PNG direto pro grupo da família
    const file = new File([this.blob], 'santinho-brasil-sobrevivente.png', { type: 'image/png' });
    if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
      btnShare.style.display = '';
      btnShare.onclick = () => {
        void navigator.share({ files: [file], title: 'Santinho — Brasil Sobrevivente' })
          .catch(() => { /* usuário desistiu do comício: sem drama */ });
      };
    }
  }

  private async copiar(msg: HTMLElement): Promise<void> {
    if (!this.blob) return;
    try {
      if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
        throw new Error('clipboard de imagem indisponível');
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': this.blob })]);
      msg.textContent = 'Santinho copiado! Cola no grupo da família.';
    } catch {
      // Plano B (navegador sem clipboard de imagem): baixa o PNG
      const a = document.createElement('a');
      const url = URL.createObjectURL(this.blob);
      a.href = url;
      a.download = 'santinho-brasil-sobrevivente.png';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      msg.textContent = 'Sem clipboard por aqui: baixamos o santinho pra você anexar.';
    }
  }
}
